/**
 * 美东（America/New_York）时间工具：4h / 日盘 noon–noon 对齐（含 DST）。
 */

export const ET_MONTH_INDEX = {
  january: 0,
  jan: 0,
  february: 1,
  feb: 1,
  march: 2,
  mar: 2,
  april: 3,
  apr: 3,
  may: 4,
  june: 5,
  jun: 5,
  july: 6,
  jul: 6,
  august: 7,
  aug: 7,
  september: 8,
  sep: 8,
  sept: 8,
  october: 9,
  oct: 9,
  november: 10,
  nov: 10,
  december: 11,
  dec: 11,
};

const ET_MONTH_SLUG = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

/** 美东本地时间 → UTC 秒（含 DST） */
export function zonedEtToUtcSec(year, monthIndex, day, hour, minute) {
  const utcGuess = Date.UTC(year, monthIndex, day, hour, minute, 0);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(new Date(utcGuess));
  const pick = (type) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(
    pick('year'),
    pick('month') - 1,
    pick('day'),
    pick('hour') === 24 ? 0 : pick('hour'),
    pick('minute')
  );
  const offsetMs = utcGuess - asUtc;
  return Math.floor((utcGuess + offsetMs) / 1000);
}

function getDatePartsInTimeZone(nowSec, timeZone) {
  const sec = Number(nowSec);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(new Date((Number.isFinite(sec) ? sec : 0) * 1000));
  const pick = (type) => Number(parts.find((p) => p.type === type)?.value);
  let hour = pick('hour');
  if (hour === 24) hour = 0;
  return {
    year: pick('year'),
    monthIndex: pick('month') - 1,
    day: pick('day'),
    hour,
    minute: pick('minute'),
  };
}

/** @returns {{ year: number, monthIndex: number, day: number, hour: number, minute: number }} */
export function getEtDateParts(nowSec) {
  return getDatePartsInTimeZone(nowSec, 'America/New_York');
}

/** 北京时间 wall parts — 兼容旧调用 / 图表中文轴。 */
export function getBeijingDateParts(nowSec) {
  return getDatePartsInTimeZone(nowSec, 'Asia/Shanghai');
}

/** UTC wall parts — 自建日内 interval 标题/副标题默认口径。 */
export function getUtcDateParts(nowSec) {
  return getDatePartsInTimeZone(nowSec, 'UTC');
}

export const UPDOWN_WINDOW_TIME_ZONE = Object.freeze({
  /** 自建日内 interval 详情/标题：UTC（产品同步口径） */
  UTC: 'UTC',
  /** 兼容旧调用；新展示请用 UTC */
  BEIJING: 'Asia/Shanghai',
  EASTERN: 'America/New_York',
});

function normalizeWindowTimeZone(timeZone) {
  if (timeZone === UPDOWN_WINDOW_TIME_ZONE.EASTERN) return UPDOWN_WINDOW_TIME_ZONE.EASTERN;
  if (timeZone === UPDOWN_WINDOW_TIME_ZONE.UTC || timeZone === 'UTC') {
    return UPDOWN_WINDOW_TIME_ZONE.UTC;
  }
  return UPDOWN_WINDOW_TIME_ZONE.BEIJING;
}

function getWindowDateParts(nowSec, timeZone) {
  const zone = normalizeWindowTimeZone(timeZone);
  if (zone === UPDOWN_WINDOW_TIME_ZONE.EASTERN) return getEtDateParts(nowSec);
  if (zone === UPDOWN_WINDOW_TIME_ZONE.UTC) return getUtcDateParts(nowSec);
  return getBeijingDateParts(nowSec);
}

/** 结算日（slug 日期）→ noon–noon 窗；roundTs = start（前一日 noon） */
export function resolveDailyUpdownWindowFromSettlementDate(year, monthIndex, day) {
  const y = Number(year);
  const m = Number(monthIndex);
  const d = Number(day);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;

  const endSec = zonedEtToUtcSec(y, m, d, 12, 0);
  // 前一日 noon：用 Date.UTC 日回退再转 ET noon
  const prev = new Date(Date.UTC(y, m, d));
  prev.setUTCDate(prev.getUTCDate() - 1);
  const startSec = zonedEtToUtcSec(
    prev.getUTCFullYear(),
    prev.getUTCMonth(),
    prev.getUTCDate(),
    12,
    0
  );
  if (!startSec || !endSec || endSec <= startSec) return null;
  return { startSec, endSec };
}

/** 当前所在日盘窗起点（ET noon → 次日 noon） */
export function resolveLiveEtNoonStartSec(nowSec) {
  const now = Number(nowSec);
  if (!Number.isFinite(now) || now <= 0) return 0;
  const parts = getEtDateParts(now);
  const todayNoon = zonedEtToUtcSec(parts.year, parts.monthIndex, parts.day, 12, 0);
  if (now >= todayNoon) return todayNoon;

  const prev = new Date(Date.UTC(parts.year, parts.monthIndex, parts.day));
  prev.setUTCDate(prev.getUTCDate() - 1);
  return zonedEtToUtcSec(prev.getUTCFullYear(), prev.getUTCMonth(), prev.getUTCDate(), 12, 0);
}

/** 当前所在 4h 窗起点（ET：0/4/8/12/16/20） */
export function resolveLiveEt4hStartSec(nowSec) {
  const now = Number(nowSec);
  if (!Number.isFinite(now) || now <= 0) return 0;
  const parts = getEtDateParts(now);
  const hourFloor = Math.floor(parts.hour / 4) * 4;
  return zonedEtToUtcSec(parts.year, parts.monthIndex, parts.day, hourFloor, 0);
}

/** 窗终点（ET noon）→ `july-22-2026` */
export function formatEtDateSlugFromSec(endSec) {
  const parts = getEtDateParts(endSec);
  const month = ET_MONTH_SLUG[parts.monthIndex];
  if (!month) return '';
  return `${month}-${parts.day}-${parts.year}`;
}

const ET_MONTH_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * 详情 / 英文标题时间窗。产品层必须显式传入时间语义：
 * interval（5m/15m/1h/4h）传 UTC；ET noon→noon 日盘传 America/New_York。
 */
export function formatUpdownPmWindowLabel(
  startSec,
  endSec,
  { timeZone = UPDOWN_WINDOW_TIME_ZONE.UTC } = {}
) {
  const start = Number(startSec);
  const end = Number(endSec);
  if (!Number.isFinite(start) || start <= 0 || !Number.isFinite(end) || end <= start) return '';

  const zone = normalizeWindowTimeZone(timeZone);
  const s = getWindowDateParts(start, zone);
  const e = getWindowDateParts(end, zone);
  const pad = (n) => String(Math.max(0, Number(n) || 0)).padStart(2, '0');
  const fmt12 = (hour, minute) => {
    const h = Number(hour) || 0;
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${pad(minute)}${period}`;
  };

  const month = ET_MONTH_LONG[s.monthIndex] || '';
  if (!month) return '';
  const tz =
    zone === UPDOWN_WINDOW_TIME_ZONE.EASTERN
      ? 'ET'
      : zone === UPDOWN_WINDOW_TIME_ZONE.UTC
      ? 'UTC'
      : 'BJT';
  if (s.year !== e.year || s.monthIndex !== e.monthIndex || s.day !== e.day) {
    const endMonth = ET_MONTH_LONG[e.monthIndex] || '';
    const startYear = s.year !== e.year ? `, ${s.year}` : '';
    const endYear = s.year !== e.year ? `, ${e.year}` : '';
    return `${month} ${s.day}${startYear}, ${fmt12(s.hour, s.minute)}-${endMonth} ${
      e.day
    }${endYear}, ${fmt12(e.hour, e.minute)} ${tz}`;
  }
  return `${month} ${s.day}, ${fmt12(s.hour, s.minute)}-${fmt12(e.hour, e.minute)} ${tz}`;
}

/** 按 locale 选中/英时间窗文案 */
export function formatUpdownWindowLabel(startSec, endSec, locale = 'en', options = {}) {
  if (
    String(locale || '')
      .toLowerCase()
      .startsWith('zh')
  ) {
    return formatUpdownFeedWindowLabel(startSec, endSec, options);
  }
  return formatUpdownPmWindowLabel(startSec, endSec, options);
}

/**
 * Feed / 详情时间窗副标题。
 * 产品层显式选择 UTC / Asia/Shanghai / America/New_York，禁止从时长猜市场类型。
 * 5m：`8月23日, UTC时间中午12:30-12:35`
 * 日盘：`8月22日, 美国东部时间中午12:00至8月23日中午12:00`
 */
export function formatUpdownFeedWindowLabel(
  startSec,
  endSec,
  { timeZone = UPDOWN_WINDOW_TIME_ZONE.UTC } = {}
) {
  const start = Number(startSec);
  const end = Number(endSec);
  if (!Number.isFinite(start) || start <= 0 || !Number.isFinite(end) || end <= start) return '';

  const zone = normalizeWindowTimeZone(timeZone);
  const s = getWindowDateParts(start, zone);
  const e = getWindowDateParts(end, zone);
  const pad = (n) => String(Math.max(0, Number(n) || 0)).padStart(2, '0');

  const to12 = (hour) => {
    const h = Number(hour) || 0;
    if (h === 0) return { period: '上午', hour: 12 };
    if (h === 12) return { period: '中午', hour: 12 };
    if (h < 12) return { period: '上午', hour: h };
    return { period: '下午', hour: h - 12 };
  };

  const a = to12(s.hour);
  const b = to12(e.hour);
  const left = `${a.period}${a.hour}:${pad(s.minute)}`;
  const right =
    a.period === b.period && a.period !== '中午'
      ? `${b.hour}:${pad(e.minute)}`
      : `${b.period}${b.hour}:${pad(e.minute)}`;
  const sep = end - start >= 4 * 3600 ? '至' : '-';
  const tzLabel =
    zone === UPDOWN_WINDOW_TIME_ZONE.EASTERN
      ? '美国东部时间'
      : zone === UPDOWN_WINDOW_TIME_ZONE.UTC
      ? 'UTC时间'
      : '北京时间';

  if (s.year !== e.year || s.monthIndex !== e.monthIndex || s.day !== e.day) {
    const startYear = s.year !== e.year ? `${s.year}年` : '';
    const endYear = s.year !== e.year ? `${e.year}年` : '';
    return `${startYear}${s.monthIndex + 1}月${s.day}日, ${tzLabel}${left}${sep}${endYear}${
      e.monthIndex + 1
    }月${e.day}日${b.period}${b.hour}:${pad(e.minute)}`;
  }

  return `${s.monthIndex + 1}月${s.day}日, ${tzLabel}${left}${sep}${right}`;
}

/**
 * @deprecated 名称含 ET 的兼容导出只负责 ET；新代码使用 formatUpdown* 并显式传 timeZone。
 */
export function formatEtUpdownPmWindowLabel(startSec, endSec) {
  return formatUpdownPmWindowLabel(startSec, endSec, {
    timeZone: UPDOWN_WINDOW_TIME_ZONE.EASTERN,
  });
}

/** @deprecated 同 formatEtUpdownPmWindowLabel。 */
export function formatEtUpdownFeedWindowLabel(startSec, endSec) {
  return formatUpdownFeedWindowLabel(startSec, endSec, {
    timeZone: UPDOWN_WINDOW_TIME_ZONE.EASTERN,
  });
}

/** @deprecated 同 formatEtUpdownPmWindowLabel。 */
export function formatEtUpdownWindowLabel(startSec, endSec, locale = 'en') {
  return formatUpdownWindowLabel(startSec, endSec, locale, {
    timeZone: UPDOWN_WINDOW_TIME_ZONE.EASTERN,
  });
}

/** 日盘 seriesKey + 窗起点 → 完整 event slug */
export function buildDailyUpdownSlugFromStart(seriesKey, startSec) {
  const key = String(seriesKey ?? '')
    .trim()
    .toLowerCase();
  const start = Number(startSec);
  if (!key || !Number.isFinite(start) || start <= 0) return '';

  const startParts = getEtDateParts(start);
  const next = new Date(Date.UTC(startParts.year, startParts.monthIndex, startParts.day));
  next.setUTCDate(next.getUTCDate() + 1);
  const endSec = zonedEtToUtcSec(
    next.getUTCFullYear(),
    next.getUTCMonth(),
    next.getUTCDate(),
    12,
    0
  );
  const dateSlug = formatEtDateSlugFromSec(endSec);
  if (!dateSlug) return '';
  return `${key}-on-${dateSlug}`;
}

export function inferYearForMonthDay(monthIndex, day) {
  const now = new Date();
  let year = now.getFullYear();
  const candidate = new Date(year, monthIndex, day);
  const diffDays = (candidate.getTime() - now.getTime()) / 86400000;
  if (diffDays > 200) year -= 1;
  if (diffDays < -200) year += 1;
  return year;
}
